import type { FilterSectionInitialLoadedEvent, FilterSectionLoadEvent, Page } from "./FilterTypes";
/*                                              */
import { FilterSectionWrapper } from "../filter/FilterSectionWrapper";
import type { Reloadable } from "../util/Reloadable";
import { Filter } from "../filter/Filter";
import { addSanErrorToPageImpression } from "../filter/FilterTracking";
import { events } from "../events";
import { forEachElement } from "../util/Utils";
import { isActive } from "../toggle/Toggle";
import { createLog } from "../util/log";

const log = createLog("reload");

export class LoadFilter {
  private static readonly FILTERSECTION_URI: string = "/leafcutter/filtersection";
  private static readonly FILTER_SECTION_WRAPPER_CLASS: string = "heureka_filterSectionWrapper";
  private static readonly FILTER_SECTION_WRAPPER_SELECTOR: string = `.${LoadFilter.FILTER_SECTION_WRAPPER_CLASS}`;
  private static readonly FILTER_SECTION_WRAPPER_LOADING_CLASS: string = "heureka_filterSectionWrapper--loading";
  private static readonly OC_FILTER_ACCORDION_SELECTOR: string =
    ".heureka_filterAccordion.heureka_filterAccordion--regular";
  private static readonly FILTER_SECTION_DYNAMIC_SECTION_ID: string = "heureka_filterDynamicSection";

  static async onLoadFilters({ rule, page, ssid, entryRule }: FilterSectionLoadEvent): Promise<void> {
    const started = performance.now();
    try {
      const node: FilterSectionWrapper = await FilterSectionWrapper.template();
      const uri = LoadFilter.uri(rule, LoadFilter.getEntryRule(rule, entryRule), page);
      log.debug("fetching filter section", { uri, ssid });
      const response: Response = await fetch(uri, {
        headers: LoadFilter.requestHeaders(ssid),
      });
      log.debug("filter section response", {
        rule,
        status: response.status,
        ms: Math.round(performance.now() - started),
      });
      const html: string = await LoadFilter.extractResponseText(response);
      return LoadFilter.replaceOcNodeContents(node, rule, html);
    } catch (e) {
      return LoadFilter.handleErrorReload(e as Error);
    }
  }

  static async onInitialLoadedFilters({ response, rule }: FilterSectionInitialLoadedEvent) {
    try {
      const node: FilterSectionWrapper = await FilterSectionWrapper.template();
      log.debug("initial filter section response", { rule, status: response?.status });
      const html: string = await LoadFilter.extractResponseText(response);
      return LoadFilter.replaceNodeContents(node, rule, html, true);
    } catch (e) {
      return LoadFilter.handleErrorInitialLoad(e as Error);
    }
  }

  private static async extractResponseText(resp: Response) {
    if (resp.status !== 200) {
      throw Error(`Failed to retrieve filtersection: ${resp.status} ${resp.statusText}`);
    }
    return resp.text();
  }

  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  private static replaceNodeContents(reloadable: Reloadable, rule: string, html: string, initialLoad = false): void {
    /*                                 */
    const range = document.createRange();
    range.selectNodeContents(reloadable.elem);

    /*                                                               */
    const fragment = range.createContextualFragment(html);
    if (initialLoad) {
      const wrapper = fragment.querySelector<HTMLElement>(LoadFilter.FILTER_SECTION_WRAPPER_SELECTOR);
      const productCount = LoadFilter.readProductCount(wrapper);
      events.filterSectionLoaded(rule, productCount);
    }
    /*                               */
    reloadable.prepare(fragment, initialLoad);

    /*           */
    range.deleteContents();
    range.insertNode(fragment);

    /*                     */
    reloadable.elem.dataset.rule = rule;

    /*                                                                       */
    window.invokePreload?.processPreloadsOnElement(reloadable.elem);
  }

  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  private static replaceOcNodeContents(reloadable: Reloadable, rule: string, html: string, initialLoad = false): void {
    const range = document.createRange();
    range.selectNodeContents(reloadable.elem);

    const fragment = range.createContextualFragment(html);
    const wrapper = fragment.querySelector<HTMLElement>(LoadFilter.FILTER_SECTION_WRAPPER_SELECTOR);
    const productCount = LoadFilter.readProductCount(wrapper);
    if (wrapper) {
      wrapper.classList.add(LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS);
    }
    reloadable.prepare(fragment, initialLoad);

    reloadable.elem.appendChild(fragment);

    /*                                                                            */
    const ocAccordionsInitialization = wrapper?.querySelector(LoadFilter.OC_FILTER_ACCORDION_SELECTOR);

    /*                                                                               */
    if (!ocAccordionsInitialization) {
      console.warn("No filter accordions found on filters reload", { rule, productCount });
      LoadFilter.removeOldFilterSectionWrappers();
      events.filterSectionLoaded(rule, productCount);
      wrapper?.classList.remove(LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS);
    } else {
      /*                                                                     */
      /*                                                                                         */
      /*                                                                                             */
      const waitingSince = performance.now();
      log.debug("waiting for oc-mount of the first filter accordion", { rule, productCount });
      ocAccordionsInitialization?.addEventListener(
        "oc-mount",
        () => {
          log.debug("oc-mount received, swapping filter sections", {
            rule,
            ms: Math.round(performance.now() - waitingSince),
          });
          LoadFilter.removeOldFilterSectionWrappers();
          events.filterSectionLoaded(rule, productCount);
          wrapper!.classList.remove(LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS);
        },
        { once: true },
      );
    }

    /*                     */
    reloadable.elem.dataset.rule = rule;

    /*                                                                       */
    window.invokePreload?.processPreloadsOnElement(reloadable.elem);
  }

  private static removeOldFilterSectionWrappers() {
    forEachElement<HTMLElement>(
      `${LoadFilter.FILTER_SECTION_WRAPPER_SELECTOR}:not(.${LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS})`,
      (wrapper) => {
        wrapper.remove();
      },
    );
  }

  private static readProductCount(wrapper?: HTMLElement | null) {
    const productCountElement = wrapper?.querySelector<HTMLElement>(".heureka_menu-bar-button [data-product-count]");
    return productCountElement?.dataset.productCount ? Number(productCountElement.dataset.productCount) : undefined;
  }

  private static handleErrorReload(e: Error): void {
    console.warn(`Filter reload encountered an error: ${e.message}`, e);
    Filter.handleError();
    addSanErrorToPageImpression("filterlist_notloaded");
    events.filterSectionLoadAborted();
  }

  private static handleErrorInitialLoad(e: Error): void {
    console.warn(`Initial filter load encountered an error: ${e.message}`, e);
    addSanErrorToPageImpression("filterlistInitial_notloaded");
    events.filterSectionLoadAborted();
  }

  private static uri(currentRule: string, entryRule: string, page: Page) {
    let url = `${LoadFilter.FILTERSECTION_URI}?rule=${encodeURIComponent(currentRule)}&entryRule=${encodeURIComponent(entryRule)}`;
    if (page.sortiertnach) {
      url = `${url}&sortiertnach=${encodeURIComponent(page.sortiertnach)}`;
    }
    return url;
  }

  private static requestHeaders(ssid?: string): HeadersInit | undefined {
    return ssid ? { "X-Heureka-SSID": ssid } : undefined;
  }

  private static getEntryRule(fallbackRule: string, nexusRule?: string) {
    if (isActive("HEUREKA_2407_MIGRATE_TO_REPTILE_NEXUS")) {
      return LoadFilter.logEntryRule("nexus selection", nexusRule || fallbackRule, fallbackRule);
    } else if (isActive("HEUREKA_2381_DONT_RETRIEVE_ENTRYRULE_FROM_HISTORY")) {
      const wrapper = document.getElementById(LoadFilter.FILTER_SECTION_DYNAMIC_SECTION_ID);
      return LoadFilter.logEntryRule(
        "data attribute of the dynamic section",
        wrapper?.dataset.entryRule || fallbackRule,
        fallbackRule,
      );
    } else {
      return LoadFilter.logEntryRule(
        "history state",
        (window.history && window.history.state && window.history.state.entryRule) || fallbackRule,
        fallbackRule,
      );
    }
  }

  private static logEntryRule(source: string, entryRule: string, fallbackRule: string) {
    log.debug("entryRule resolved", { source, entryRule, usedFallback: entryRule === fallbackRule });
    return entryRule;
  }
}
